import { UserCog } from "lucide-react";
import { useState } from "react";
import {
  type Identifier,
  useGetList,
  useListContext,
  useNotify,
  useRefresh,
  useTranslate,
  useUpdateMany,
} from "ra-core";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

import type { Sale, Task } from "../types";

/**
 * Hands the selected tasks to another account manager.
 *
 * The whole point of an administrator seeing every manager's tasks is being
 * able to move them, so the owner is picked once and applied to the selection
 * in a single write.
 */
export const TaskBulkReassignButton = () => {
  const translate = useTranslate();
  const notify = useNotify();
  const refresh = useRefresh();
  const { selectedIds = [], onUnselectItems } = useListContext<Task>();
  const [open, setOpen] = useState(false);
  const [salesId, setSalesId] = useState<string>("");
  const [updateMany, { isPending }] = useUpdateMany();

  const { data: sales } = useGetList<Sale>(
    "sales",
    {
      pagination: { page: 1, perPage: 100 },
      sort: { field: "last_name", order: "ASC" },
    },
    { enabled: open },
  );

  const handleReassign = () => {
    if (!salesId) return;
    /*
      The Select works in strings; sales.id is a bigint. Writing the string
      straight through hands PostgREST "3" for a bigint column, so resolve the
      chosen row back to its own identifier instead of casting.
    */
    const owner: Identifier | undefined = sales?.find(
      (sale) => String(sale.id) === salesId,
    )?.id;
    if (owner == null) return;
    updateMany(
      "tasks",
      { ids: selectedIds, data: { sales_id: owner } },
      {
        onSuccess: () => {
          notify("ra.notification.updated", {
            type: "info",
            messageArgs: { smart_count: selectedIds.length },
          });
          setOpen(false);
          setSalesId("");
          onUnselectItems();
          refresh();
        },
        onError: () => notify("ra.notification.http_error", { type: "error" }),
      },
    );
  };

  return (
    <>
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="h-9"
        onClick={() => setOpen(true)}
      >
        <UserCog />
        {translate("resources.tasks.bulk.reassign")}
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {translate("resources.tasks.actions.reassign")}
            </DialogTitle>
          </DialogHeader>
          <Select value={salesId} onValueChange={setSalesId}>
            <SelectTrigger
              className="w-full"
              aria-label={translate("resources.tasks.fields.sales_id")}
            >
              <SelectValue
                placeholder={translate("resources.tasks.fields.sales_id")}
              />
            </SelectTrigger>
            <SelectContent>
              {sales?.map((sale) => (
                <SelectItem key={sale.id} value={String(sale.id)}>
                  {sale.first_name} {sale.last_name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <DialogFooter>
            <Button
              type="button"
              disabled={!salesId || isPending}
              onClick={handleReassign}
            >
              {translate("ra.action.confirm")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
